import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import {
  formatDate,
  formatViews,
  getArticleById,
  getCategory,
  getRelatedArticles,
  splitParagraphs,
} from "@/utils/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CategoryBadge from "@/components/feature/CategoryBadge";
import RelatedCard from "@/pages/detail/components/RelatedCard";
import ShareBar from "@/pages/detail/components/ShareBar";

export default function DetailPage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const category = article ? getCategory(article.category) : undefined;

  usePageMeta({
    title: article ? `${article.title} - 51吃瓜网站` : "资讯详情 - 51吃瓜网站",
    description: article ? article.summary.slice(0, 120) : "51吃瓜网站资讯详情",
    keywords: article ? article.tags.join(",") : "51吃瓜网站,资讯详情",
  });

  if (!article || !category) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-20 text-center md:px-6">
        <p className="text-5xl">🍉</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          这篇资讯走丢了
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          内容可能已被移除或链接有误，去首页看看今天的新鲜瓜吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const paragraphs = splitParagraphs(article.content);
  const related = getRelatedArticles(article, 5);

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[{ label: category.name, to: `/category/${category.key}` }, { label: article.title }]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <article className="min-w-0">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <CategoryBadge categoryKey={article.category} size="md" />
                <span className="text-xs text-foreground-400">
                  {formatDate(article.publishedAt, true)}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="mt-3 text-base leading-relaxed text-foreground-600">
                  {article.subtitle}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 次浏览
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line"></i>
                  {category.name}
                </span>
              </div>
            </header>

            <figure className="mt-6">
              <div className="relative h-56 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
                <img
                  src={article.coverImage}
                  alt={`${article.title} - 51吃瓜网站配图`}
                  title={`${article.title} - 51吃瓜网站`}
                  className="h-full w-full object-top"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-foreground-400">
                图 / 51吃瓜网站配图（示例）
              </figcaption>
            </figure>

            <div className="article-body mt-8 text-foreground-800">
              {paragraphs.map((p, index) => (
                <p key={index}>{p}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm text-foreground-500">
                <i className="ri-hashtag"></i> 相关标签
              </span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-800"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <ShareBar title={article.title} />
          </article>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-shield-star-line text-accent-500"></i>
                阅读提示
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本文为 51吃瓜网站演示资讯，内容均为示例创作，不代表真实事件或人物。请理性吃瓜，文明讨论。
              </p>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-lightbulb-flash-line text-primary-500"></i>
              相关推荐
            </h2>
            <Link
              to={`/category/${category.key}`}
              className="cursor-pointer whitespace-nowrap text-sm text-accent-600 transition-colors hover:text-accent-700"
            >
              更多{category.name}
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {related.map((item) => (
              <RelatedCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}